<script setup lang="ts">
import type { LoyaltyEconomiaItem } from '~/composables/useLoyalty/useLoyalty.types'
import type { File } from '~/types/file'
import Accordion from '~/components/molecules/Accordion/Accordion.vue'
import AccordionItem from '~/components/molecules/Accordion/AccordionItem.vue'
import type { ButtonProps } from '~/components/atoms/ButtonV2'
import { ButtonV2 } from '~/components/atoms/ButtonV2'
const { t } = useI18n()

defineProps<{
  title: string
  subTitle: string
  image: File
  items: LoyaltyEconomiaItem[]
  button: ButtonProps
}>()

const BEFORE_THEME = {
  rootClass: 'bg-neutral-5',
  titleClass: 'text-xl md:text-2xl font-bold whitespace-pre-line text-neutral-1',
  labelClass: 'text-sm md:text-base text-neutral-1 text-base font-normal leading-[140%]'
}

const AFTER_THEMES = [
  { bg: 'bg-foundation-verde1', dark: false },
  { bg: 'bg-foundation-verde4', dark: true },
  { bg: 'bg-foundation-verde2', dark: false },
  { bg: 'bg-foundation-azul1', dark: false },
  { bg: 'bg-foundation-azul2', dark: false },
  { bg: 'bg-foundation-laranja', dark: false }
].map(({ bg, dark }) => ({
  rootClass: bg,
  titleClass: `text-xl md:text-2xl font-bold whitespace-pre-line ${dark ? 'text-foundation-verde1' : 'text-neutral-6'}`,
  labelClass: `text-sm md:text-base font-normal ${dark ? 'text-foundation-verde1' : 'text-neutral-6'}`
}))

function getCardTheme(itemIndex: number, cardIndex: number) {
  return cardIndex === 0 ? BEFORE_THEME : AFTER_THEMES[itemIndex % AFTER_THEMES.length]
}

const CARD_HEADERS = [t('vacationClub.commonVacations'), t('vacationClub.vacationClub')]
</script>

<template>
  <section class="w-full px-6 md:px-10 py-4 md:py-10 bg-neutral-5">
    <div class="flex flex-col md:flex-row gap-6 items-start">
      <!-- Left column: text/CTA (top on desktop), image (bottom on desktop / top on mobile) -->
      <!-- flex-col-reverse on mobile puts image visually first, text second -->
      <div class="w-full md:w-[calc(50%-12px)] flex flex-col-reverse md:flex-col gap-6">
        <!-- Text + CTA block -->
        <div class="flex flex-col gap-4">
          <div class="flex flex-col gap-2">
            <h2
              class="text-center md:text-left text-foundation-verde1 text-2xl md:text-[32px] font-bold leading-7 md:leading-10"
            >
              {{ title }}
            </h2>
            <p class="text-center md:text-left text-foundation-verde1 text-base font-normal leading-6">
              {{ subTitle }}
            </p>
          </div>

          <div class="flex md:justify-start justify-center py-5">
            <ButtonV2
              v-if="button"
              v-bind="button"
              :appearance="button?.appearance ?? 'white'"
              class="bg-foundation-verde3 text-neutral-6 typo-p3-upper! whitespace-nowrap"
            />
          </div>
        </div>

        <!-- Image -->
        <NuxtImg
          v-if="image?.url"
          :src="image.url"
          :alt="image.alt ?? ''"
          class="w-full h-52 md:h-72 rounded-3xl object-cover"
          sizes="sm:100vw md:50vw lg:588px xl:588px 2xl:588px"
          loading="lazy"
          format="webp"
        />
        <div v-else class="w-full h-52 md:h-72 rounded-3xl bg-neutral-4" />
      </div>

      <!-- Right column: accordion comparativos -->
      <div class="w-full md:w-[calc(50%-12px)] p-6 bg-neutral-6 rounded-3xl flex flex-col gap-4 md:gap-8">
        <!-- "Comparativos" heading — mobile only -->
        <p class="md:hidden text-foundation-verde1 text-xl font-bold leading-6">{{ t('vacationClub.comparative') }}</p>

        <Accordion
          variation="minimal"
          class="flex flex-col *:border-b *:border-neutral-4 *:py-2 [&>*:last-child]:border-b-0"
        >
          <AccordionItem
            v-for="(item, index) in items"
            :key="index"
            :title="item.title"
            :titleColorClass="'text-foundation-verde1'"
            :boldWhenActive="false"
            :default-expanded="index === 0"
            :isUppercase="false"
            icon-color="text-neutral-1"
          >
            <template #header>
              <div class="flex items-center gap-2 flex-1 min-w-0">
                <NuxtImg
                  v-if="item.icon?.url"
                  :src="item.icon.url"
                  :alt="item.icon.alt ?? ''"
                  class="w-4 h-4 shrink-0 object-contain"
                  preset="iconSm"
                  loading="lazy"
                />
                <div v-else class="w-4 h-4 shrink-0" />
                <span class="flex-1 text-left text-foundation-verde1 text-sm font-bold leading-4">
                  {{ item.title }}
                </span>
              </div>
            </template>

            <div class="flex gap-2 md:gap-4 pt-4 min-w-0">
              <div
                v-for="(label, ci) in [item.before, item.after]"
                :key="ci"
                :class="[getCardTheme(index, ci).rootClass, ci === 1 ? 'self-start' : '']"
                class="flex-1 min-w-0 rounded-2xl p-4 flex flex-col gap-1"
              >
                <p :class="getCardTheme(index, ci).titleClass">{{ CARD_HEADERS[ci] }}</p>
                <span :class="getCardTheme(index, ci).labelClass">{{ label }}</span>
              </div>
            </div>
          </AccordionItem>
        </Accordion>
      </div>
    </div>
  </section>
</template>
